/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useState } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap, jsx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import CustomizeIcon from '@atlaskit/icon/core/customize';
import { Popup } from '@atlaskit/popup/popup';
import type { PopupProps } from '@atlaskit/popup/types';
import { Box } from '@atlaskit/primitives/compiled';

import type { ColorMode, FeatureFlagSwitcherConfig, SectionIds } from '../types';
import AppearanceControlsForm from './form';

const styles = cssMap({
	formContainer: {
		maxWidth: '410px',
	},
});

export type AppearanceControlsButtonProps = {
	colorMode?: ColorMode;
	onColorModeChange?(colorMode: ColorMode): void;
	popupPlacement?: PopupProps['placement'];
	testId?: string;
	hiddenSections?: SectionIds;
	featureFlagSwitchers?: FeatureFlagSwitcherConfig[];
};

/**
 * __Appearance controls button__
 *
 * The button that triggers the appearance controls to appear.
 */
const AppearanceControlsButton: ({
	colorMode,
	onColorModeChange,
	popupPlacement,
	testId,
	hiddenSections,
	featureFlagSwitchers,
}: AppearanceControlsButtonProps) => JSX.Element = ({
	colorMode,
	onColorModeChange,
	popupPlacement,
	testId,
	hiddenSections,
	featureFlagSwitchers,
}: AppearanceControlsButtonProps) => {
	const [isOpen, setIsOpen] = useState(false);

	const handleClick = useCallback(() => {
		setIsOpen((current) => !current);
	}, []);

	let indicatorText = '';

	return (
		<Popup
			isOpen={isOpen}
			onClose={() => setIsOpen(false)}
			placement={popupPlacement}
			testId={testId}
			shouldFlip={false}
			content={() => (
				<Box xcss={styles.formContainer}>
					<AppearanceControlsForm
						colorMode={colorMode}
						onColorModeChange={onColorModeChange}
						testId={testId ? `${testId}--form` : undefined}
						hiddenSections={hiddenSections}
						featureFlagSwitchers={featureFlagSwitchers}
					/>
				</Box>
			)}
			trigger={(triggerProps) => (
				<Button
					{...triggerProps}
					iconBefore={CustomizeIcon}
					iconAfter={(iconProps) => <ChevronDownIcon {...iconProps} size="small" />}
					appearance="subtle"
					isSelected={isOpen}
					onClick={handleClick}
					testId={testId ? `${testId}--button` : undefined}
				>
					Styles{indicatorText}
				</Button>
			)}
			shouldRenderToParent
		/>
	);
};

export default AppearanceControlsButton;
